import React, { useState } from 'react';
import { WizardState } from '../../../hooks/useZakatWizard';
import { Input } from '../../ui/Input';
import { Button } from '../../ui/Button';
import { Card } from '../../ui/Card';
import { Mail, Lock, CheckCircle2, Shield, FileText, Bell } from 'lucide-react';
import { motion } from 'framer-motion';
interface EmailStepProps {
  state: WizardState;
  updateState: (updates: Partial<WizardState>) => void;
  totals: {
    grossAssets: number;
    totalLiabilities: number;
    netAssets: number;
  };
  onSubmit: () => void;
  onSkip: () => void;
}
export function EmailStep({
  state,
  updateState,
  totals,
  onSubmit,
  onSkip
}: EmailStepProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [consentChecked, setConsentChecked] = useState(true);
  const formatCurrency = (val: number) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(val);
  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!state.email.trim()) {
      setError('Please enter your email address');
      return;
    }
    if (!isValidEmail(state.email)) {
      setError('Please enter a valid email address');
      return;
    }
    setIsSubmitting(true);
    // Simulate API call - in production, this would send to your backend
    // Include consentChecked in the payload
    await new Promise(resolve => setTimeout(resolve, 1000));
    updateState({
      emailSubmitted: true
    });
    setIsSubmitting(false);
    onSubmit();
  };
  const handleSkip = () => {
    updateState({
      emailSubmitted: true
    });
    onSkip();
  };
  return <div className="space-y-6" data-id="element-307">
      <div className="text-center mb-8" data-id="element-308">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-teal-100 text-[#2D9596] mb-4" data-id="element-309">
          <FileText className="w-8 h-8" data-id="element-310" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 font-serif mb-2" data-id="element-311">
          Receive your full Zakat report and join the community.
        </h2>
        <p className="text-gray-600 max-w-md mx-auto" data-id="element-312">
          Get your personalized calculation delivered to your inbox, plus join
          thousands of Muslims building wealth the right way.
        </p>
      </div>

      {/* Preview Card */}
      <Card className="bg-gray-50 border-gray-200" data-id="element-313">
        <div className="space-y-3" data-id="element-314">
          <div className="flex justify-between items-center" data-id="element-315">
            <span className="text-gray-600" data-id="element-316">Total Assets</span>
            <span className="font-semibold text-gray-900" data-id="element-317">
              {formatCurrency(totals.grossAssets)}
            </span>
          </div>
          <div className="flex justify-between items-center" data-id="element-318">
            <span className="text-gray-600" data-id="element-319">Total Liabilities</span>
            <span className="font-semibold text-red-600" data-id="element-320">
              -{formatCurrency(totals.totalLiabilities)}
            </span>
          </div>
          <div className="flex justify-between items-center pt-3 border-t border-gray-200" data-id="element-321">
            <span className="font-bold text-gray-900" data-id="element-322">
              Net Zakatable Wealth
            </span>
            <span className="font-bold text-lg text-gray-900" data-id="element-323">
              {formatCurrency(totals.netAssets)}
            </span>
          </div>

          {/* Blurred Zakat Amount */}
          <div className="flex justify-between items-center pt-3 border-t border-gray-200" data-id="element-324">
            <span className="font-bold text-[#2D9596]" data-id="element-325">Your Zakat (2.5%)</span>
            <div className="relative" data-id="element-326">
              <span className="text-2xl font-bold text-[#2D9596] blur-md select-none" data-id="element-327">
                $X,XXX
              </span>
              <div className="absolute inset-0 flex items-center justify-center" data-id="element-328">
                <Lock className="w-5 h-5 text-gray-400" data-id="element-329" />
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Email Form */}
      <form onSubmit={handleSubmit} className="space-y-4" data-id="element-330">
        <div className="relative" data-id="element-331">
          <Input label="Email Address" type="email" placeholder="your@email.com" value={state.email} onChange={e => updateState({
          email: e.target.value
        })} error={error} className="pl-10" data-id="element-332" />
          <Mail className="absolute left-3 top-[38px] w-5 h-5 text-gray-400" data-id="element-333" />
        </div>

        {/* Consent Checkbox */}
        <label className="flex items-start gap-3 cursor-pointer group" data-id="element-334">
          <div className="relative mt-0.5" data-id="element-335">
            <input type="checkbox" checked={consentChecked} onChange={e => setConsentChecked(e.target.checked)} className="sr-only peer" data-id="element-336" />
            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${consentChecked ? 'bg-[#2D9596] border-[#2D9596]' : 'border-gray-300 group-hover:border-gray-400'}`} data-id="element-337">
              {consentChecked && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-338">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-339" />
                </svg>}
            </div>
          </div>
          <span className="text-sm text-gray-600 leading-snug" data-id="element-340">
            Send me my calculation and occasional insights on Sharia-compliant
            wealth building from the PIF team.
          </span>
        </label>

        <Button type="submit" className="w-full py-4 text-base" isLoading={isSubmitting} data-id="element-341">
          {isSubmitting ? 'Sending...' : 'Send My Report & Join PIF'}
        </Button>

        {/* Skip Option */}
        <div className="text-center" data-id="element-342">
          <button type="button" onClick={handleSkip} className="text-sm text-gray-400 hover:text-gray-600 transition-colors underline underline-offset-2" data-id="element-343">
            Skip for now
          </button>
        </div>
      </form>

      {/* Trust Signals */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4" data-id="element-344">
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="flex items-center gap-3 text-sm text-gray-500" data-id="element-345">
          <Shield className="w-5 h-5 text-teal-600 flex-shrink-0" data-id="element-346" />
          <span data-id="element-347">Your data is private & secure</span>
        </motion.div>
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="flex items-center gap-3 text-sm text-gray-500" data-id="element-348">
          <Mail className="w-5 h-5 text-teal-600 flex-shrink-0" data-id="element-349" />
          <span data-id="element-350">Relevant insights, no spam</span>
        </motion.div>
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="flex items-center gap-3 text-sm text-gray-500" data-id="element-351">
          <Bell className="w-5 h-5 text-teal-600 flex-shrink-0" data-id="element-352" />
          <span data-id="element-353">Annual Zakat reminders</span>
        </motion.div>
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="flex items-center gap-3 text-sm text-gray-500" data-id="element-354">
          <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0" data-id="element-355" />
          <span data-id="element-356">Instant delivery</span>
        </motion.div>
      </div>

      {/* What you'll receive */}
      <div className="bg-teal-50 rounded-xl p-5 mt-6" data-id="element-357">
        <h4 className="font-semibold text-gray-900 mb-3" data-id="element-358">
          Your report includes:
        </h4>
        <ul className="space-y-2 text-sm text-gray-700" data-id="element-359">
          <li className="flex items-start gap-2" data-id="element-360">
            <CheckCircle2 className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" data-id="element-361" />
            <span data-id="element-362">Complete breakdown of your zakatable assets</span>
          </li>
          <li className="flex items-start gap-2" data-id="element-363">
            <CheckCircle2 className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" data-id="element-364" />
            <span data-id="element-365">
              Zakat calculation using both Gold & Silver Nisab standards
            </span>
          </li>
          <li className="flex items-start gap-2" data-id="element-366">
            <CheckCircle2 className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" data-id="element-367" />
            <span data-id="element-368">Current gold & silver prices used in calculation</span>
          </li>
          <li className="flex items-start gap-2" data-id="element-369">
            <CheckCircle2 className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" data-id="element-370" />
            <span data-id="element-371">PDF-ready format for your records</span>
          </li>
        </ul>
      </div>
    </div>;
}